import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit, OnDestroy, ViewEncapsulation, Renderer2, HostListener } from '@angular/core';
import { ActivatedRoute, RouterOutlet, RouterModule, Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { HeaderComponent } from './header/header.component';
import { NavigationComponent } from './navigation/navigation.component';
import { CloudWatchRumService } from './services/cloudwatch-rum.service';
import { debounceTime, fromEvent, of, merge, map, Observable, Subscription, filter } from 'rxjs';
import { SessionTimeoutModalComponent } from './session-timeout-modal/session-timeout-modal.component';
import { ModalStateService } from './services/modal-state.service';
import { ChatComponent } from './chat/chat.component';
import { environment } from '../../environments/environment';
import { QuicksightService } from './services/user/quicksight.service';
import { TokenPropertyService } from './services/token-property.service';
import { SettingsService } from './services/user/settings.service';
import { ChatEligibilityService } from './services/chat-eligibility.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [HeaderComponent, NavigationComponent, RouterOutlet, RouterModule, CommonModule, SessionTimeoutModalComponent, ChatComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  encapsulation: ViewEncapsulation.None
})
export class AppComponent implements OnInit , OnDestroy  {
  title = 'Alight Worklife Insights';
  showHeader: boolean = true;
  inactiveTime: number = 15 * 60 * 1000; // 15 minute timeout
  data: string = '';
  currentUrl: any = ''
  private noheaderurl: any[] = ['/logoff','/sso-error'];
  private inactivetimeoutsub: Subscription = new Subscription(); 
  private navigationendsub: Subscription = new Subscription();
  private tokenSub: Subscription = new Subscription();
  isChatModalOpen = false;
  modalHasOpened = false;
  showAIButton = false;

  constructor(
    private router: Router,
    private cloudWatchRumService: CloudWatchRumService,
    private modalStateService: ModalStateService,
    private route: ActivatedRoute,
    private renderer: Renderer2,
    private settingsService: SettingsService,
    private tokenPropertyService: TokenPropertyService,
    private quicksightService: QuicksightService,
    private chatEligibilityService: ChatEligibilityService
  ) {
    this.updateShowAIButton();
  }

  /**
   * Determines if the AI button (chat) should be shown based on environment and client code
   */
  updateShowAIButton(): void {
    // Use the centralized chat eligibility service
    this.showAIButton = this.chatEligibilityService.isChatEligible();
    
    // Log debug information for development
    const debugInfo = this.chatEligibilityService.getEligibilityDebugInfo();
    this.logInfo('Chat visibility debug:', debugInfo);
  }

  ngOnInit() {
    // Subscribe to token availability
    this.subscribeToTokenAvailability();

    this.route.queryParams.subscribe(params => {
      if (params['admin'] === 'true') {
        this.router.navigate(['/admin'])
      }
    });
    
    // Clear any existing RUM session cookies before initializing to ensure fresh session
    this.cloudWatchRumService.clearRumSession();
    this.cloudWatchRumService.initializeRum();
    
    this.navigationendsub = this.getNavigationEndUrl().subscribe((url: any) => {
      this.currentUrl = url;
      // Unsubscribe from previous inactive timeout subscription to prevent memory leaks
      if (this.inactivetimeoutsub) {
        this.inactivetimeoutsub.unsubscribe();
      }
      this.inactivetimeoutsub = this.InactiveTimeOut(this.inactiveTime).subscribe(() => {
        if (!this.noheaderurl.includes(this.currentUrl)) {
          // Emit RUM custom event for session timeout
          this.cloudWatchRumService.addCustomEvent('session_timeout', {
            url: this.currentUrl,
            inactivityTime: this.inactiveTime,
          });
          this.modalStateService.openModal(SessionTimeoutModalComponent);
        }
      });
      this.hideHeader(url);
    });

    // Re-evaluate chat visibility when tokens are available (client code may change)
    this.tokenPropertyService.getTokenAvailability().subscribe(() => {
      // Force the QuickSight service to refresh its tokens to clear any cached client code
      this.quicksightService.refreshTokens();
      this.updateShowAIButton();
    });
  }

    /**
   * Log helper methods that only log in non-production environments
   */
  private logInfo(message: string, ...data: any[]): void {
    if (!environment.production) {
      console.log(`[app.component.ts] ${message}`, ...data);
    }
  }

  hideHeader(currentUrl: any): void {
    if (this.noheaderurl.includes(currentUrl)) {
      this.showHeader = false;
    } else {
      this.showHeader = true;
    }
  }    

  InactiveTimeOut(time: number): Observable<any> {
    const activityIndicatorEvents = ['mousemove', 'keypress','touchstart','blur','scroll','click'];
    return merge(...activityIndicatorEvents.map(eventName => fromEvent(window, eventName)),of('load'))
      .pipe(
        debounceTime(time)
      )
  }

  getNavigationEndUrl(): Observable<string> {
    return this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event: NavigationEnd) => event.url))
  }
  
  /**
   * Loads user settings from the API
   */
  loadUserSettings(): void {
    this.settingsService.getUserSettings().subscribe({
      next: (settings) => {
        // Settings already logged in the service
      },
      error: (error) => {
        console.error('Error loading user settings:', error);
      }
    });
  }
  
  /**
   * Subscribes to token availability and loads user settings only when tokens are available
   */
  subscribeToTokenAvailability(): void {
    // Use the proper public method to access token availability
    this.tokenSub = this.tokenPropertyService.getTokenAvailability().subscribe(isAvailable => {
      if (isAvailable) {
        this.logInfo('Tokens available, loading user settings');
        this.loadUserSettings();
      }
    });
  }
  
  onOpenChat() {
    // Only open the chat if the AI button is enabled
    if (this.showAIButton) {
      this.isChatModalOpen = true;
      this.modalHasOpened = true; // Set the flag to true when the chat modal is opened
      // Add class to body when modal opens to prevent scrolling
      this.renderer.addClass(document.body, 'body-modal-open');
    }
  }

  onCloseChat() {
    this.isChatModalOpen = false; // This triggers the closing animation
    
    // Remove the body class
    this.renderer.removeClass(document.body, 'body-modal-open');
  }

  /**
   * Handles browser/tab close events to record session end in RUM
   * Uses beforeunload event to capture user closing browser or tab
   */
  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent): void {
    // Record browser/tab close event
    // Note: This must be synchronous and quick to work reliably
    try {
      // Only record if user has an active session (tokens present)
      const hasToken = localStorage.getItem('id_token') || localStorage.getItem('token');
      if (hasToken) {
        this.cloudWatchRumService.recordLogoutEvent(false, 'browser_tab_close');
      }
    } catch (error) {
      console.error('Error recording browser close event:', error);
    }
    // Don't prevent default or show confirmation dialog
  }

  ngOnDestroy() {
    this.inactivetimeoutsub.unsubscribe();
    this.navigationendsub.unsubscribe();
    this.tokenSub.unsubscribe();
  }
 }

